
import Vue from "vue";
import permissions from "../config/actions-fields.json";
import rules from "@/services/helpers/validation-rules";

export default Vue.extend({
  name: "Services",

  props: {
    value: {
      required: true,
      default: []
    },
    showGuarantee: {
      type: Boolean,
      default: false,
      required: false
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false
    },
    status: {
      required: false,
      type: Object,
      default: () => ({} as any)
    },
    oldValue: {
      required: false,
      type: Object,
      default: () => ({})
    },
    newServices: {
      required: false,
      default: () => ({})
    },
    isEdit: {
      required: false,
      default: false,
      type: Boolean
    },
    extraOutputs: {
      required: false,
      type: [Number, String],
      default: 0
    },
    serviceTypeId: {
      required: false,
      type: Number
    },
    errorMessages: {
      required: false,
      default: () => ({})
    },
    guarantyRequired: {
      required: false,
      default: true,
      type: Boolean
    },
    beneficiary: {
      required: true,
      default: () => ({} as any),
      type: Object
    },
    showVatRequired: {
      type: Boolean,
      default: false,
      required: false
    }
  },

  data: () => ({
    rules,
    model: [] as Array<any>,
    goods: [] as Array<any>,
    outputs: null as any,
    releaseMode: null as any,
    ataExpositon: null as any,
    oldModel: null as any,
    services: {} as any,
    forceKey: 0 as number,
    fullLoaded: false as boolean,
    guaranty: true as any,
    servicesVatTotalSum: 0 as number,
    servicesTotalSum: 0 as number,
    totalPayedServiceSUm: 0 as number,
    totalRemainedForPayServiceSUm: 0 as number
  }),

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler() {
        this.model = this.value;
        this.calculateSums();
        this.$forceUpdate();
      }
    },
    guarantyRequired: {
      immediate: true,
      handler() {
        this.guaranty = this.guarantyRequired;
      }
    },
    guaranty: {
      immediate: true,
      handler() {
        this.$emit("setGuaranty", this.guaranty);
      }
    },
    oldValue: {
      immediate: true,
      handler() {
        this.oldModel = this.oldValue;
      }
    },
    dataEditSum: {
      immediate: true,
      handler() {
        if (this.isDataChanged && this.extraVisitsAfter === 0) {
          this.$emit("dataEdit", {
            service_id: this.services?.data_edit?.value,
            sum_no_vat: this.dataEditSum,
            sum_with_vat: this.addVat(this.dataEditSum),
            quantity: 1
          });
        } else {
          this.$emit("dataEdit", null);
        }

        this.$emit("additionalSumChange", this.additionalSum);
      }
    },
    additionalSum: {
      immediate: true,
      handler() {
        this.$emit("additionalSumChange", this.additionalSum);
      }
    },
    goods: {
      immediate: true,
      deep: true,
      handler() {
        if (this.goods && this.goods.length) {
          this.setGoodsService();
        }
      }
    },
    outputs: {
      immediate: true,
      handler() {
        this.setOutputsService();
      }
    },
    beneficiary: {
      deep: true,
      handler() {
        this.setBeneficiaryService();
      }
    },
    releaseMode: {
      immediate: true,
      handler() {
        if (this.releaseMode) {
          this.setReleaseModeService();
        }
      }
    },
    extraOutputs: {
      immediate: true,
      handler() {
        this.setExtraVisitsAfterService();
      }
    },
    servicesVatTotalSum: {
      immediate: true,
      handler() {
        this.$emit("setOrderSum", this.servicesVatTotalSum);
      }
    }
  },

  computed: {
    extraVisitsAfterSum() {
      return (
        this.getServicePrice(
          this.services.extra_visits_after,
          Number(this.extraOutputs)
        ) || 0
      );
    },
    extraVisitsAfter() {
      return Number(this.extraOutputs);
    },
    dataEditSum() {
      if (
        this.fullLoaded &&
        this.isDataChanged &&
        this.extraVisitsAfter === 0
      ) {
        return this.getServicePrice(this.services.data_edit);
      }
      return 0;
    },
    additionalSum() {
      return this.extraVisitsAfterSum + this.dataEditSum;
    },
    isDataChanged() {
      if (this.model && this.oldModel) {
        return JSON.stringify(this.model) !== JSON.stringify(this.oldModel);
      }
      return false;
    },
    canAddExtraServices() {
      return this.status?.alias === "issued" && this.serviceTypeId === 1;
    },
    goodsSum() {
      return this.model.reduce(
        (
          total: number,
          { quantity, price }: { price: number; quantity: number }
        ) => {
          if (quantity && price) {
            return total + Math.ceil(price * quantity * 100) / 100;
          }
          return total;
        },
        0
      );
    }
  },

  async mounted() {
    await this.getServices();

    this.$nextTick(() => {
      this.fullLoaded = true;

      this.setBeneficiaryService();
    });
  },

  methods: {
    async getServices(): Promise<void> {
      try {
        const orderServices = await this.$API
          .services()
          .getListByServiceType(this.serviceTypeId);

        for (const service of orderServices) {
          orderServices[service.alias] = service;
        }

        this.services = { ...orderServices };

        if (this.isEdit) {
          this.model.forEach((item: any) => {
            if (item.alias) {
              item.editable = this.services[item.alias].editable;
            }
          });
        }
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message || e);
      }
    },
    setCciMemberService(): void {
      const serviceKey = `cci_member`;

      if (this.serviceTypeId !== 2) {
        return;
      }

      if (this.beneficiary.cci_member) {
        const price = this.getServicePrice(this.services[serviceKey]);
        if (this.services[serviceKey]) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: this.addVat(price),
            quantity: 1,
            name: this.services[serviceKey].text
          };
          this.checkService(serviceKey, service);
        }
      } else {
        this.removeService(serviceKey);
      }
    },
    setGoodsService() {
      const serviceKey = "extra_positions";
      if (this.goods.length > 1) {
        const price = this.getServicePrice(
          this.services[serviceKey],
          this.goods.length,
          1
        );
        if (this.services.extra_positions) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: this.addVat(price),
            quantity: this.goods.length - 1,
            name: this.services[serviceKey].text
          };

          this.checkService(serviceKey, service);
        }
      } else {
        this.removeService(serviceKey);
      }
    },
    setBeneficiaryService() {
      if (!this.fullLoaded) {
        return;
      }

      if (!this.beneficiary) {
        this.removeService("_normal", true);
        return;
      }

      if (this.serviceTypeId === 1) {
        const serviceKey = `${this.beneficiary.type}_normal`;
        this.removeService("_normal", true);
        const price = this.getServicePrice(this.services[serviceKey]);

        let vatPrice = price;

        if (this.beneficiary.type !== "physical") {
          vatPrice = this.addVat(price);
        }

        if (this.services[serviceKey]) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: vatPrice,
            quantity: 1,
            name: this.services[serviceKey].text
          };
          this.checkService(serviceKey, service);
          this.setReleaseModeService();
        }
      }

      if (this.serviceTypeId === 2) {
        this.setAAIJService();
        this.setCciMemberService();
      }

      if (this.serviceTypeId === 3) {
        this.setCertificationService();
      }

      if (this.serviceTypeId === 4) {
        this.setEvaluationService();
      }

      if (this.serviceTypeId === 5) {
        this.setExpertizeService();
      }

      this.calculateSums();
    },
    setReleaseModeService() {
      if (this.services && this.releaseMode === "urgent") {
        this.$emit("remove", [
          this.services.juridical_urgent?.value,
          this.services.physical_urgent?.value
        ]);
        const serviceKey = `${this.beneficiary.type}_urgent`;
        const price = this.getServicePrice(this.services[serviceKey]);

        let vatPrice = price;

        if (this.beneficiary.type !== "physical") {
          vatPrice = this.addVat(price);
        }
        if (this.services[serviceKey]) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: vatPrice,
            quantity: 1,
            name: this.services[serviceKey].text
          };
          this.checkService(serviceKey, service);
        }
      } else if (this.services) {
        this.$emit("remove", [
          this.services.juridical_urgent?.value,
          this.services.physical_urgent?.value
        ]);
      }
    },
    setOutputsService() {
      const serviceKey = `extra_visit`;

      if (this.outputs && this.outputs > 1) {
        const price = this.getServicePrice(
          this.services[serviceKey],
          this.outputs - 1
        );

        if (this.services[serviceKey]) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: this.addVat(price),
            quantity: this.outputs - 1,
            name: this.services[serviceKey].text
          };
          this.checkService(serviceKey, service);
        }
      } else {
        this.removeService(serviceKey);
      }
    },
    setExtraVisitsAfterService() {
      const serviceKey = `extra_visits_after`;
      if (Number(this.extraOutputs) > 0) {
        const price = this.getServicePrice(
          this.services[serviceKey],
          Number(this.extraOutputs)
        );

        if (this.services[serviceKey]) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: this.addVat(price),
            quantity: this.extraVisitsAfter,
            name: this.services[serviceKey].text
          };
          this.$emit("extraVisitsAfter", service);
        }
      } else {
        this.$emit("extraVisitsAfter", null);
      }
    },
    checkService(serviceKey: string, service: any): void {
      if (
        !this.model.some((item: any) => item.service_id === service.service_id)
      ) {
        this.model.push({ ...service, alias: serviceKey });
      } else {
        const serviceIndex = this.model.findIndex(
          (item: any) => item.service_id === service.service_id
        );

        this.model[serviceIndex] = {
          sum_with_vat: service.sum_with_vat,
          sum_no_vat: service.sum_no_vat,
          quantity: service.quantity,
          editable: this.services[serviceKey]?.editable,
          alias: this.services[serviceKey].alias,
          ...this.model[serviceIndex]
        };

        this.$emit("input", this.model);
      }
    },
    setAAIJService() {
      if (this.serviceTypeId === 2) {
        const serviceKey = `aaij`;
        const price = this.getServicePrice(this.services.aaij);

        if (this.services[serviceKey]) {
          const service = {
            service_id: this.services[serviceKey].value,
            sum_no_vat: price,
            sum_with_vat: this.addVat(price),
            quantity: 1,
            name: this.services[serviceKey].text
          };
          this.checkService(serviceKey, service);
        }
      }
    },
    setCertificationService() {
      const serviceKey = "cert";
      const price = this.getServicePrice(this.services[serviceKey]);

      if (this.services[serviceKey]) {
        const service = {
          service_id: this.services[serviceKey].value,
          sum_no_vat: price,
          sum_with_vat: this.addVat(price),
          quantity: 1,
          name: this.services[serviceKey].text
        };
        this.checkService(serviceKey, service);
      }
    },
    setExpertizeService() {
      const serviceKey = "exp";
      const price = this.getServicePrice(this.services[serviceKey]);

      if (this.services[serviceKey]) {
        const service = {
          service_id: this.services[serviceKey].value,
          sum_no_vat: price,
          sum_with_vat: this.addVat(price),
          quantity: 1,
          name: this.services[serviceKey].text,
          editable: this.services[serviceKey]?.editable,
          alias: serviceKey
        };

        this.checkService(serviceKey, service);
      }
    },
    setEvaluationService() {
      const serviceKey = "eval";
      const price = this.getServicePrice(this.services[serviceKey]);

      if (this.services[serviceKey]) {
        const service = {
          service_id: this.services[serviceKey].value,
          sum_no_vat: price,
          alias: serviceKey,
          sum_with_vat: this.addVat(price),
          quantity: 1,
          name: this.services[serviceKey].text
        };
        this.checkService(serviceKey, service);
      }
    },
    removeService(serviceKey: string, isDynamicKey = false): void {
      if (isDynamicKey) {
        this.$emit("remove", [
          this.services.juridical_normal?.value,
          this.services.physical_normal?.value
        ]);
      } else {
        this.$emit("remove", this.services[serviceKey]?.value);
      }
    },
    addVat(sum: number): number {
      if (!this.guaranty) {
        return sum;
      }
      return Math.ceil((sum + (sum * 20) / 100) * 100) / 100;
    },
    setGuaranteePrice(): void {
      // if (this.guarantyRequired) {
      //   this.model.required_guaranty_sum =
      //     Math.trunc(0.3 * this.goodsSum) || 0;
      // }
    },
    getServicePrice(service: any, value = 1, minusCount = 0) {
      if (service && service.values) {
        return (
          Number(
            service.values.find((el: any) => value >= el.min && value <= el.max)
              ?.value
          ) *
          (value - minusCount)
        );
      }
      return 0;
    },
    async previewReceipt() {
      try {
        const file = await this.$API
          .invoices()
          .getFileForPrint(Number(this.$route.params.id));
        if (file && file.url) {
          this.downloadFile(file);
        }
      } catch (e) {
        await this.$store.dispatch("alert/showError", e.message);
      }
    },
    toggleDeleteService(service: any): void {
      service._isDeleted = !service._isDeleted;
      this.$forceUpdate();
    },
    can(action: string, subject: string): boolean {
      return (
        permissions.actions.includes(action) &&
        !permissions.subject.includes(subject)
      );
    },
    downloadFile(file: any) {
      const element = document.createElement("a");
      element.setAttribute("href", file.url);
      element.setAttribute("target", "_blank");
      element.click();
    },
    changeReleaseService(): void {
      const service = this.services[`${this.beneficiary.type}_urgent`];

      if (service && this.release_mode === "urgent") {
        this.model.push({
          name: service.text,
          service_id: service.value,
          sum_no_vat: this.getServicePrice(service),
          sum_with_vat: this.addVat(this.getServicePrice(service)),
          quantity: 1
        });
      } else if (service && this.release_mode === "normal") {
        const serviceIndex = this.model.findIndex(
          (item: any) => item.alias === `${this.beneficiary.type}_urgent`
        );
        if (serviceIndex !== -1) {
          this.model?.splice(serviceIndex, 1);
        }
      }
      this.forceKey++;
    },
    updateVat(index: number, sum: any): void {
      const sum_no_vat = Number(sum);
      const sum_with_vat = this.addVat(sum_no_vat);

      if (!this.model[index].paid_sum) {
        this.model[index].paid_sum = 0;
      }

      this.model[index] = {
        ...this.model[index],
        sum_no_vat,
        sum_with_vat,
        remained_sum: sum_with_vat - this.model[index].paid_sum
      };
      this.$emit("input", this.model);
      this.calculateSums();
      this.$forceUpdate();
    },
    canEditService(service: any): boolean {
      const cciDiscountServiceId = this.services.cci_member?.value;
      return (
        service.editable &&
        !this.model?.some(
          (item: any) => item.service_id === cciDiscountServiceId
        )
      );
    },
    changeServicePrice() {
      if (!this.guaranty) {
        this.model.forEach((item: any) => {
          item.sum_with_vat = item.sum_no_vat;
          item.remained_sum = item.sum_with_vat - item.paid_sum;
        });
      } else {
        this.model.forEach((item: any) => {
          item.sum_with_vat = this.addVat(item.sum_no_vat);
          item.remained_sum = item.sum_with_vat - item.paid_sum;
        });
      }

      this.calculateSums();
      // this.$emit("input", this.model);
      // this.$forceUpdate();
    },
    calculateSums(): void {
      this.servicesVatTotalSum = this.model.reduce(
        (total: number, { sum_with_vat }: { sum_with_vat: number }) => {
          if (sum_with_vat) {
            return total + Number(sum_with_vat);
          }
          return total;
        },
        0
      );

      this.servicesTotalSum = this.model.reduce(
        (total: number, { sum_no_vat }: { sum_no_vat: number }) => {
          if (sum_no_vat) {
            return total + Number(sum_no_vat);
          }
          return total;
        },
        0
      );

      this.totalPayedServiceSUm = this.model.reduce(
        (total: number, { paid_sum }: { paid_sum: number }) => {
          if (paid_sum) {
            return total + Number(paid_sum);
          }
          return total;
        },
        0
      );

      this.totalRemainedForPayServiceSUm = this.model.reduce(
        (total: number, { remained_sum }: { remained_sum: number }) => {
          if (remained_sum) {
            return total + Number(remained_sum);
          }
          return total;
        },
        0
      );

      this.$emit("setOrderSum", this.servicesVatTotalSum);
    }
  }
});
